import {getGameCompanies} from "../models/game.js";
import {getCompanyChampionships, getCompanyEvents, getCompanyRosters} from "../models/company.js";
import {getRosterFactions, getRosterGimmicks, getRosterSuperstars, getRosterTagTeams} from "../models/roster.js";
import {getTagTeamSuperstars} from "../models/tagTeam.js";
import {getScenarioGame} from "../models/scenario.js";
import {getTopEvent} from "./events.js";
import {compareChampionships, getChampionshipHolder, getUniqueChampionships} from "./championships.js";
import {getRosterMemberPopularity} from "./helpers.js";
import {getSuperstarCurrentGimmick} from "../models/superstar.js";

async function getScenarioGameData(scenario) {
    // Used to create game from local scenario. Duplicates all objects, removing id keys. todo test might have to delete keys instead of using object destructuring

    let gameData = {"companies": []};

    const game = await getScenarioGame(scenario);
    const { $loki, scenarioId, ...newGame } = game;
    gameData.gameDetails = newGame;

    const gameCompanies = await getGameCompanies(game);
    for (const company of gameCompanies) {
        let companyData = {"rosters": []};
        const { $loki, ...newCompany } = company;
        companyData.companyDetails = newCompany;

        let companyEvents = await getCompanyEvents(company);
        companyEvents = companyEvents.map(({$loki, ...keepAttrs}) => keepAttrs);
        companyData.events = companyEvents;

        let companyChampionships = await getCompanyChampionships(company);
        // Add superstar uid
        for (const championship of companyChampionships) {
            championship.superstarUid = championship.superstarId;
            delete championship.$loki;
        }
        companyData.championships = companyChampionships;

        const companyRosters = await getCompanyRosters(company);
        for (const roster of companyRosters) {
            let rosterData = {};

            let rosterSuperstars = await getRosterSuperstars(roster);
            for (const superstar of rosterSuperstars) {
                superstar.uid = superstar.$loki;
                delete superstar.$loki;
            }
            rosterData.superstars = rosterSuperstars;

            let rosterTagTeams = await getRosterTagTeams(roster);
            // Add superstar uids
            for (const tagTeam of rosterTagTeams) {
                const tagTeamSuperstars = await getTagTeamSuperstars(tagTeam);
                tagTeam.superstarUids = tagTeamSuperstars.map(x => x.$loki);
                delete tagTeam.$loki;
            }
            rosterData.tagTeams = rosterTagTeams;

            companyData.rosters.push(rosterData);
        }

        gameData.companies.push(companyData)
    }

    return gameData;
}

async function getScenarioCompanyInformation(company, competitor = false) {  // get_scenario_company_information
    const data = {};
    const events = await getCompanyEvents(company);
    const topEvent = getTopEvent(events);

    data['companyName'] = company.name;
    data['reputation'] = getCompanyReputationFromFans(company.fans);
    data['fans'] = company.fans;
    data['points'] = company.points;
    data['ownerName'] = company.ownerName;
    data['style'] = company.style;
    data['topEvent'] = {'name': topEvent.name, 'prestige': topEvent.prestige};

    if (competitor) {
        data['rosters'] = [];
        let companyRosters = await getCompanyRosters(company);
        // List of rosters: main and training rosters
        const rosters = [[companyRosters.find(x => x.type === 'main')],
            companyRosters.filter(x => ['training', 'trainingAvailable',].includes(x.type))]
        for (const x of rosters) {
            const rosterInformation = await getScenarioRosterInformation(x, competitor);
            data['rosters'].push(rosterInformation);
        }

        data['championships'] = await  getScenarioChampionshipsInformation(company);
        //data['brands'] = getScenarioBrandsInformation(company); todo
    }

    return data;
}

async function getScenarioRosterInformation(rosters, competitor) {  // get_scenario_roster_information
    // Get number of superstars, tag teams, trios, retired and future superstars, top workers

    const data = {};
    const superstars = (await Promise.all(rosters.map(x => getRosterSuperstars(x)))).flat();
    const allTagTeams = (await Promise.all(rosters.map(x => getRosterTagTeams(x)))).flat();
    const tagTeams = allTagTeams.filter(x => !x.trio);
    const trios = allTagTeams.filter(x => x.trio);
    const factions = (await Promise.all(rosters.map(x => getRosterFactions(x)))).flat();
    const managerIds = superstars.filter(x => x.managerId).map(x => x.managerId);
    const managers = superstars.filter(x => x.fullTimeManager || managerIds.includes(x.$loki));

    // Superstars
    const activeSuperstars = superstars.filter(x => !x.retired && !x.monthToAppearIn && !x.fullTimeManager);
    data['topSuperstars'] = getTopSuperstars(activeSuperstars);
    if (rosters[0].type === 'main') {
        const retiredSuperstars = superstars.filter(x => x.retired);
        data['retiredSuperstars'] = getTopSuperstars(retiredSuperstars);
    } else {
        const futureSuperstars = superstars.filter(x => x.monthToAppearIn);
        data['futureSuperstars'] = getTopSuperstars(futureSuperstars);
    }

    // Stats (gimmicks, graphics, hot/over)
    data['numberOfSuperstars'] = superstars.length;
    data['numberOfSuperstarsWithGraphics'] = superstars.filter(
        x => x.graphic && typeof x.graphic === 'object' && Object.keys(x.graphic).length !== 0 && !x.graphic['empty_graphic']
    ).length;
    data['numberOfSuperstarsWithTraits'] = superstars.filter(x => x.traits && x.traits.length).length;

    if (rosters[0].type === 'main' && !competitor) {
        const rosterGimmicks = (await Promise.all(rosters.map(x => getRosterGimmicks(x)))).flat();
        if (rosterGimmicks.length) {
            data['numberOfSuperstarsWithGimmicks'] = superstars.filter(x =>
                rosterGimmicks.find(y => y.superstarId === x.$loki)).length;
        } else {
            data['numberOfSuperstarsWithGimmicks'] = (await Promise.all(superstars.filter(x =>
                getSuperstarCurrentGimmick(x)))).flat().length;
        }
        data['numberOfFaceSuperstars'] = superstars.filter(x => x.alignment === 'face').length;
        data['numberOfHeelSuperstars'] = superstars.filter(x => x.alignment === 'heel').length;
        data['numberOfOverSuperstars'] = superstars.filter(x => x.overness >= 80).length;
        data['numberOfStaleSuperstars'] = superstars.filter(x => x.overness < 20).length;
    }

    // Teams
    data['topTagTeam'] = await getTopTeams(tagTeams);
    data['topTrio'] = await getTopTeams(trios);
    data['numberOfTagTeams'] = tagTeams.length;
    data['numberOfTrios'] = trios.length;
    data['numberOfFactions'] = factions.length;
    data['numberOfManagers'] = managers.length;
    data['type'] = rosters[0].type;

    function getTopSuperstars(l) {
        const sortedSuperstars = l.sort((a, b) => b.popularity - a.popularity);
        return sortedSuperstars.slice(0, 3).map(x => x.name);
    }

    async function getTopTeams(l) {
        let sortedTeams = [...l];
        for (const x of sortedTeams) {
            let s = await getTagTeamSuperstars(x)
            if (s.length) {
                x.tempPopularity = await getRosterMemberPopularity(x, s)
            }
        }
        sortedTeams = sortedTeams.filter(x => x.tempPopularity !== undefined).sort((a, b) => b.tempPopularity - a.tempPopularity);
        for (const x of sortedTeams) {
            delete x.tempPopularity;
        }
        return sortedTeams.length ? sortedTeams[0].name : null;
    }

    return data;
}

async function getScenarioChampionshipsInformation(company) {  // get_scenario_championships_information
    // Get number of championships, graphics, top 2 championships' names
    const data = {};
    const championships = await getUniqueChampionships((await getCompanyChampionships(company)).filter(x => !x.matchContract));
    const sortedChampionships = championships.sort(compareChampionships);

    data['topChampionships'] = [];
    for (const x of sortedChampionships.slice(0, 2)) {
        const holder = await getChampionshipHolder(x);
        let championName = holder ? holder.name : null;
        data['topChampionships'].push({'name': x.name, 'championName': championName});
    }

    data['numberOfChampionships'] = championships.length;
    data['numberOfChampionshipsWithGraphics'] = championships
        .filter(x => x.graphic && typeof x.graphic === 'object' && Object.keys(x.graphic).length !== 0).length;

    return data;
}

function getCompanyReputationFromFans(fans) {  // get_company_reputation_from_fans
    const fansData = [0, 500, 1100, 2300, 5000, 10500, 22000, 47000, 99000, 212000, 451000];
    const reputation = fansData.find(x => fans < x) ?? 451000;
    return fansData.indexOf(reputation);
}


export { getScenarioGameData, getScenarioCompanyInformation }